import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import { Navigate } from "react-router-dom";
import { Stepper } from "../components/ui/stepper";
import { listModelli, previewModello } from "../utils/modelliFatturaApi";
import { buildContestoFattura, risolviVariabili } from "../utils/variabiliFattura";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { toastSdiError } from "../utils/sdiErrorMessages";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  ExternalLink,
  Plus,
  Trash2,
  RefreshCw,
  Send,
  Check,
  Receipt,
  User,
  FileText,
  Eye,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIMI_IVA = [
  { value: "esente_art10", label: "Esente Art.10" },
  { value: "iva_4", label: "IVA 4%" },
  { value: "iva_22", label: "IVA 22%" },
  { value: "esclusa_art15", label: "Escl. Art.15" },
];

const STEPS = [
  { id: "dati", label: "Dati principali" },
  { id: "righe", label: "Dettagli e righe" },
  { id: "anteprima", label: "Anteprima" },
  { id: "invio", label: "Invio" },
];

const MESI_NOME = {
  "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile",
  "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto",
  "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre",
};

function formatMese(mese) {
  if (!mese) return "";
  const [anno, m] = mese.split("-");
  return `${MESI_NOME[m] || m} ${anno}`;
}

// Pannello anteprima laterale (sempre visibile)
function AnteprimePanel({ fascicolo, voci, mese, scadenzaGiorni, causale, note, struttura }) {
  const now = new Date();
  const dataEmissione = now.toLocaleDateString("it-IT");
  const scadenza = new Date(now.getTime() + scadenzaGiorni * 86400000).toLocaleDateString("it-IT");

  const contesto = useMemo(
    () => buildContestoFattura(fascicolo, mese, struttura),
    [fascicolo, mese, struttura]
  );

  const causaleRisolta = useMemo(
    () => (causale ? risolviVariabili(causale, contesto) : ""),
    [causale, contesto]
  );
  const noteRisolte = useMemo(
    () => (note ? risolviVariabili(note, contesto) : ""),
    [note, contesto]
  );

  const vociRisolte = useMemo(
    () =>
      voci.map((v) => ({
        ...v,
        descrizione: v.descrizione ? risolviVariabili(v.descrizione, contesto) : v.descrizione,
      })),
    [voci, contesto]
  );

  const totaleImponibile = vociRisolte.reduce(
    (acc, v) => acc + (parseFloat(v.quantita) || 1) * (parseFloat(v.prezzo_unitario) || 0),
    0
  );
  const anno = mese ? mese.split("-")[0] : now.getFullYear().toString();

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden text-xs">
      {/* Header fattura preview */}
      <div className="bg-slate-50 border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <span className="font-semibold text-slate-700">Anteprima fattura</span>
        <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-0">Bozza</Badge>
      </div>

      <div className="p-4 space-y-3">
        {/* Logo placeholder */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Da</p>
            <p className="font-semibold text-slate-800 text-xs">{struttura?.name || "Struttura"}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-emerald-600 text-sm">FATTURA</p>
            <p className="text-slate-500 text-[10px]">FT-{anno}-XXXX</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-500">
          <div>
            <span className="text-slate-400">Data:</span>{" "}
            <span className="text-slate-700">{dataEmissione}</span>
          </div>
          <div>
            <span className="text-slate-400">Scadenza:</span>{" "}
            <span className="text-slate-700">{scadenza}</span>
          </div>
        </div>

        {fascicolo && (
          <div className="bg-slate-50 rounded-lg p-2.5">
            <p className="text-[10px] text-slate-400 mb-1">A</p>
            <p className="font-medium text-slate-800 text-xs">
              {fascicolo.nome_completo ||
                `${fascicolo.anagrafica?.cognome || ""} ${fascicolo.anagrafica?.nome || ""}`.trim()}
            </p>
            {fascicolo.anagrafica?.codice_fiscale && (
              <p className="text-[10px] text-slate-500">
                C.F. {fascicolo.anagrafica.codice_fiscale}
              </p>
            )}
          </div>
        )}

        {causaleRisolta && (
          <div>
            <p className="text-[10px] text-slate-400 mb-0.5">Causale</p>
            <p className="text-slate-700 text-[11px]">{causaleRisolta}</p>
          </div>
        )}

        {noteRisolte && (
          <div>
            <p className="text-[10px] text-slate-400 mb-0.5">Note</p>
            <p className="text-slate-700 text-[11px]">{noteRisolte}</p>
          </div>
        )}

        {/* Righe */}
        {vociRisolte.length > 0 ? (
          <div>
            <div className="flex items-center justify-between py-1 border-b border-slate-100 text-[10px] text-slate-400 font-medium">
              <span>Descrizione</span>
              <span>Importo</span>
            </div>
            {vociRisolte.map((v, i) => (
              <div key={i} className="flex items-start justify-between py-1.5 border-b border-slate-50">
                <span className="text-[11px] text-slate-700 flex-1 pr-2 leading-snug">
                  {v.descrizione || "—"}
                </span>
                <span className="text-[11px] font-medium text-slate-800 shrink-0">
                  €{(
                    (parseFloat(v.quantita) || 1) * (parseFloat(v.prezzo_unitario) || 0)
                  ).toFixed(2)}
                </span>
              </div>
            ))}
            <div className="pt-2 space-y-0.5">
              <div className="flex justify-between text-[10px] text-slate-500">
                <span>Imponibile</span>
                <span>€{totaleImponibile.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs font-semibold text-slate-800 pt-1 border-t border-slate-200">
                <span>Totale</span>
                <span className="text-emerald-600">€{totaleImponibile.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-[10px] text-slate-400 text-center py-2">
            Le righe appariranno qui quando selezioni un modello e un ospite.
          </p>
        )}
      </div>
    </div>
  );
}

export default function NuovaFattura() {
  const { user, token, activeStruttura } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canAccess = canAccessFatturazioneElettronica(user);

  const [currentStep, setCurrentStep] = useState(0);

  // Step 1 — Dati principali
  const [fascicolo, setFascicolo] = useState(null);
  const [fascicoli, setFascicoli] = useState([]);
  const [fascSearch, setFascSearch] = useState("");
  const [showFascSearch, setShowFascSearch] = useState(false);
  const [modelli, setModelli] = useState([]);
  const [modelloSelezionato, setModelloSelezionato] = useState(null);
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [causale, setCausale] = useState("");
  const [note, setNote] = useState("");
  const [scadenzaGiorni, setScadenzaGiorni] = useState(30);
  const [struttura, setStruttura] = useState(null);

  // Step 2 — Righe (precompilate da retta, Prima Nota, deblistering, ecc.)
  const [voci, setVoci] = useState([]);
  const [loadingVoci, setLoadingVoci] = useState(false);
  const [vociModificate, setVociModificate] = useState(false);
  const [avvisiVoci, setAvvisiVoci] = useState([]);

  // Step 3 — Anteprima PDF
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Step 4 — Invio
  const [inviaSdi, setInviaSdi] = useState(false);
  const [inviaEmail, setInviaEmail] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedFattura, setGeneratedFattura] = useState(null);

  // Carica fascicoli (solo attivi: esclusi archiviati)
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/fascicoli-assistenziali`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 200 },
      })
      .then((res) => {
        const list = res.data?.fascicoli || res.data || [];
        setFascicoli(
          (Array.isArray(list) ? list : []).filter((f) => f.archiviata !== true),
        );
      })
      .catch(() => {});
  }, [token, activeStruttura?.id]);

  // Carica modelli
  useEffect(() => {
    if (!token) return;
    listModelli(token, { tab: "miei", perPage: 50 })
      .then((res) => setModelli(res.modelli || []))
      .catch(() => {});
  }, [token, activeStruttura?.id]);

  // Carica struttura attiva
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/struttura`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setStruttura(res.data?.struttura || res.data))
      .catch(() => {});
  }, [token, activeStruttura?.id]);

  useEffect(() => {
    const m = searchParams.get("mese");
    if (m && /^\d{4}-\d{2}$/.test(m)) setMese(m);
  }, [searchParams]);

  // Precarica fascicolo da query param
  useEffect(() => {
    const fid = searchParams.get("fascicoloId");
    if (!fid || fascicoli.length === 0) return;
    const f = fascicoli.find((x) => (x._id || x.id) === fid);
    if (f) setFascicolo(f);
  }, [searchParams, fascicoli]);

  // Precarica modello "Retta mensile" se arriva da fascicolo
  useEffect(() => {
    if (!searchParams.get("fascicoloId") || modelli.length === 0 || modelloSelezionato) return;
    const retta = modelli.find((m) => m.tipo === "retta" && m.is_base);
    if (retta) {
      setModelloSelezionato(retta);
      setCausale(retta.causale || "");
      setNote(retta.note_default || "");
      setScadenzaGiorni(retta.scadenza_offset_giorni ?? 30);
    }
  }, [searchParams, modelli, modelloSelezionato]);

  // Quando si seleziona un modello, popola causale/note/scadenza
  const handleSelezionaModello = (m) => {
    setModelloSelezionato(m);
    if (m) {
      setCausale(m.causale || "");
      setNote(m.note_default || "");
      setScadenzaGiorni(m.scadenza_offset_giorni ?? 30);
    }
  };

  // Carica voci dal sistema (retta + extra del mese)
  const loadVociDaSistema = useCallback(async () => {
    if (!fascicolo || !mese || !token) return;
    const fascicoloId = fascicolo._id || fascicolo.id;
    if (!fascicoloId) return;
    setLoadingVoci(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/fatturazione/anteprima/${fascicoloId}`,
        { headers: { Authorization: `Bearer ${token}` }, params: { mese } },
      );
      const data = res.data || {};
      setVoci(
        (data.voci || []).map((v) => ({
          descrizione: v.descrizione || "",
          quantita: v.quantita ?? 1,
          prezzo_unitario: v.prezzo_unitario ?? 0,
          regime_iva: v.regime_iva || "iva_22",
          tipo: v.tipo,
        })),
      );
      setAvvisiVoci(data.avvisi || []);
      setVociModificate(false);
    } catch (err) {
      setAvvisiVoci([]);
      if (modelloSelezionato) {
        try {
          const preview = await previewModello(token, modelloSelezionato.id, fascicoloId, mese);
          setVoci(
            (preview.righe || []).map((r) => ({
              descrizione: r.descrizione || "",
              quantita: r.quantita ?? 1,
              prezzo_unitario: r.prezzo_unitario ?? 0,
              regime_iva: r.regime_iva || "iva_22",
            })),
          );
          if (preview.scadenza_offset_giorni) setScadenzaGiorni(preview.scadenza_offset_giorni);
        } catch {
          toast.error(err?.response?.data?.detail || "Impossibile caricare le voci della fattura");
        }
      } else {
        toast.error(err?.response?.data?.detail || "Impossibile caricare le voci della fattura");
      }
    } finally {
      setLoadingVoci(false);
    }
  }, [token, fascicolo, mese, modelloSelezionato]);

  useEffect(() => {
    if (!fascicolo || !mese || !token) {
      setVoci([]);
      setAvvisiVoci([]);
      return;
    }
    loadVociDaSistema();
  }, [fascicolo, mese, token, loadVociDaSistema, activeStruttura?.id]);

  const contestoPreview = useMemo(
    () => buildContestoFattura(fascicolo, mese, struttura),
    [fascicolo, mese, struttura]
  );

  const causaleAnteprima = useMemo(
    () => (causale && fascicolo ? risolviVariabili(causale, contestoPreview) : null),
    [causale, fascicolo, contestoPreview]
  );

  const handleAnteprimaPdf = useCallback(async () => {
    if (!fascicolo || !mese || !token) return;
    const fascicoloId = fascicolo._id || fascicolo.id;
    if (!fascicoloId) return;
    if (voci.length === 0) {
      toast.error("Aggiungi almeno una voce prima di generare l'anteprima PDF");
      return;
    }

    const vociPayload = voci.map((v) => ({
      descrizione: v.descrizione
        ? risolviVariabili(v.descrizione, contestoPreview)
        : v.descrizione,
      quantita: parseFloat(v.quantita) || 1,
      prezzo_unitario: parseFloat(v.prezzo_unitario) || 0,
      regime_iva: v.regime_iva || "iva_22",
    }));
    const noteAnteprima = note ? risolviVariabili(note, contestoPreview) : null;

    setPdfLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fatturazione/anteprima/${fascicoloId}/pdf-preview`,
        {
          mese,
          voci: vociPayload,
          causale: causaleAnteprima || null,
          note: noteAnteprima || null,
          scadenza_giorni: scadenzaGiorni,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      setPdfBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
      setPdfDialogOpen(true);
    } catch (e) {
      let msg = "Impossibile generare l'anteprima PDF";
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const t = await data.text();
          const j = JSON.parse(t);
          if (j.detail) msg = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        } catch {
          /* ignore */
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setPdfLoading(false);
    }
  }, [fascicolo, mese, token, voci, note, causaleAnteprima, contestoPreview, scadenzaGiorni]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const handlePdfDialogChange = (open) => {
    setPdfDialogOpen(open);
    if (!open) {
      setPdfBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  };

  const handleAvanti = async () => {
    if (currentStep === 0) {
      if (!fascicolo) { toast.error("Seleziona un ospite"); return; }
      if (!mese) { toast.error("Seleziona il mese"); return; }
      if (fascicolo && voci.length === 0) {
        await loadVociDaSistema();
      }
      if (voci.length === 0) {
        toast.error("Nessuna voce da fatturare per questo ospite e mese");
        return;
      }
    }
    setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const handleIndietro = () => setCurrentStep((s) => Math.max(0, s - 1));

  const handleGeneraFattura = async () => {
    if (!fascicolo || !mese) { toast.error("Dati incompleti"); return; }
    if (voci.length === 0) {
      toast.error("Aggiungi almeno una voce alla fattura");
      return;
    }
    const fascicoloId = fascicolo._id || fascicolo.id;
    setGenerating(true);
    try {
      const vociPayload = voci.map((v) => ({
        descrizione: v.descrizione,
        quantita: parseFloat(v.quantita) || 1,
        prezzo_unitario: parseFloat(v.prezzo_unitario) || 0,
        regime_iva: v.regime_iva || "iva_22",
      }));

      const payload = {
        ...(vociModificate
          ? {
              mese,
              modello_id: modelloSelezionato?.id || null,
              voci_manuali: vociPayload,
              includi_retta: false,
              includi_prima_nota: false,
              includi_deblistering: false,
              includi_deposito_cauzionale: false,
              scadenza_giorni: scadenzaGiorni,
            }
          : {
              mese,
              modello_id: modelloSelezionato?.id || null,
              includi_retta: true,
              includi_prima_nota: true,
              includi_deblistering: true,
              includi_deposito_cauzionale: false,
              scadenza_giorni: scadenzaGiorni,
            }),
        causale_override: causaleAnteprima || causale || null,
        note_override: note ? risolviVariabili(note, contestoPreview) : null,
      };

      const res = await axios.post(
        `${API_URL}/api/fatturazione/fatture/${fascicoloId}/genera`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fattura = res.data;
      let fatturaFinal = fattura;

      if (inviaSdi && fattura.fattura_id) {
        try {
          const sdiRes = await axios.post(
            `${API_URL}/api/fatturazione/fatture/${fattura.fattura_id}/invia-sdi`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (sdiRes.data?.success) {
            fatturaFinal = { ...fattura, numero: sdiRes.data.numero || fattura.numero, _sdiInviata: true };
            toast.success(`Fattura ${fatturaFinal.numero} inviata a SDI`);
          } else {
            fatturaFinal = { ...fattura, _sdiInviata: false };
            toast.warning("Bozza creata, ma l'invio SDI non è riuscito");
            toastSdiError(sdiRes.data?.message_technical || sdiRes.data?.message, {
              userMessage: sdiRes.data?.message,
            });
          }
        } catch (err) {
          fatturaFinal = { ...fattura, _sdiInviata: false };
          toast.warning("Bozza creata, ma l'invio SDI non è riuscito");
          const d = err.response?.data;
          toastSdiError(d?.message_technical || d?.detail || d?.message, {
            userMessage: d?.message || (typeof d?.detail === "string" ? d.detail : undefined),
          });
        }
      } else {
        toast.success(`Bozza creata — approva per assegnare il numero definitivo`);
      }

      setGeneratedFattura(fatturaFinal);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nella generazione della fattura");
    } finally {
      setGenerating(false);
    }
  };

  const filteredFascicoli = fascicoli.filter((f) => {
    if (!fascSearch) return true;
    const name = (
      f.nome_completo ||
      `${f.anagrafica?.cognome || ""} ${f.anagrafica?.nome || ""}`.trim()
    ).toLowerCase();
    return name.includes(fascSearch.toLowerCase());
  });

  const addVoce = () => {
    setVociModificate(true);
    setVoci((prev) => [
      ...prev,
      { descrizione: "", quantita: 1, prezzo_unitario: 0, regime_iva: "iva_22" },
    ]);
  };

  const removeVoce = (idx) => {
    setVociModificate(true);
    setVoci((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateVoce = (idx, field, value) => {
    setVociModificate(true);
    setVoci((prev) => prev.map((v, i) => (i === idx ? { ...v, [field]: value } : v)));
  };

  if (!canAccess) return <Navigate to="/fatturazione" replace />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/fatturazione" className="hover:text-[#4CAF50] transition-colors">
              Fatturazione
            </Link>
            <span>/</span>
            <span className="text-slate-800 font-medium">Nuova fattura</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-slate-500"
            onClick={() => navigate("/fatturazione")}
          >
            <X className="w-3.5 h-3.5 mr-1.5" />
            Esci
          </Button>
        </div>
      </div>

      {/* Header wizard */}
      <div className="bg-white border-b border-slate-100 px-4 py-4">
        <div className="max-w-screen-xl mx-auto">
          <h1 className="text-xl font-bold text-slate-900 mb-1">Nuova fattura</h1>
          <p className="text-xs text-slate-500 mb-4">
            La fattura viene precompilata con retta ed extra del mese (Prima Nota, deblistering, servizi).
          </p>
          <Stepper
            steps={STEPS}
            currentStep={currentStep}
            onStepChange={setCurrentStep}
          />
        </div>
      </div>

      {/* Body a 2 colonne */}
      <div className="flex-1 px-4 py-6">
        <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row gap-6">
          {/* Colonna sx — form */}
          <div className="flex-1 min-w-0">
            {/* ─────────── Step 0 — Dati principali ─────────── */}
            {currentStep === 0 && (
              <Card>
                <CardContent className="p-6 space-y-5">
                  <h2 className="text-sm font-semibold text-slate-800">Dati principali</h2>

                  {/* Cliente / Ospite */}
                  <div className="space-y-2">
                    <Label className="text-xs">Cliente / Ospite *</Label>
                    {fascicolo ? (
                      <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">
                            {fascicolo.nome_completo ||
                              `${fascicolo.anagrafica?.cognome || ""} ${fascicolo.anagrafica?.nome || ""}`.trim()}
                          </p>
                          {fascicolo.anagrafica?.codice_fiscale && (
                            <p className="text-[10px] text-slate-500">
                              Fascicolo: {fascicolo.anagrafica.codice_fiscale}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => setShowFascSearch(true)}
                        >
                          Cambia
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full text-xs justify-start gap-2 h-10 text-slate-500"
                        onClick={() => setShowFascSearch(true)}
                      >
                        <Search className="w-3.5 h-3.5" />
                        Cerca e seleziona ospite...
                      </Button>
                    )}
                  </div>

                  {/* Modello di fattura */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Modello di fattura</Label>
                      <Link
                        to="/fatturazione/modelli"
                        className="text-[10px] text-[#4CAF50] hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Gestisci modelli
                      </Link>
                    </div>
                    <Select
                      value={modelloSelezionato?.id || "none"}
                      onValueChange={(v) => {
                        if (v === "none") { handleSelezionaModello(null); return; }
                        const m = modelli.find((x) => x.id === v);
                        handleSelezionaModello(m || null);
                      }}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Seleziona un modello..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" className="text-xs text-slate-400">
                          Nessun modello (voci manuali)
                        </SelectItem>
                        {modelli.map((m) => (
                          <SelectItem key={m.id} value={m.id} className="text-xs">
                            {m.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date e numero */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Data fattura *</Label>
                      <Input
                        type="date"
                        defaultValue={new Date().toISOString().slice(0, 10)}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Numero fattura</Label>
                      <Input
                        value="Progressivo automatico"
                        readOnly
                        className="h-9 text-xs text-slate-400 bg-slate-50 cursor-not-allowed"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Data scadenza</Label>
                      <Input
                        type="date"
                        value={new Date(Date.now() + scadenzaGiorni * 86400000)
                          .toISOString()
                          .slice(0, 10)}
                        readOnly
                        className="h-9 text-xs text-slate-400 bg-slate-50 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Mese di competenza */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Mese di competenza *</Label>
                    <Input
                      type="month"
                      value={mese}
                      onChange={(e) => setMese(e.target.value)}
                      className="h-9 text-xs w-48"
                    />
                  </div>

                  {/* Causale */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Causale
                      <span className="text-slate-400 font-normal ml-1.5">
                        (puoi usare variabili <code className="text-[10px] bg-slate-100 px-1 rounded">{"{{...}}"}</code>)
                      </span>
                    </Label>
                    <Input
                      value={causale}
                      onChange={(e) => setCausale(e.target.value)}
                      placeholder="Es. Retta di degenza mese di {{periodo.mese_anno}}"
                      className="h-9 text-xs"
                    />
                    {causaleAnteprima && causaleAnteprima !== causale && (
                      <p className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5">
                        <span className="font-medium">Anteprima:</span> {causaleAnteprima}
                      </p>
                    )}
                  </div>

                  {/* Note */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">Note (visibili in fattura)</Label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Note opzionali..."
                      rows={2}
                      className="text-xs resize-none"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ─────────── Step 1 — Dettagli e righe ─────────── */}
            {currentStep === 1 && (
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-800">Dettagli e righe</h2>
                    {modelloSelezionato && fascicolo && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={loadVociDaSistema}
                        disabled={loadingVoci}
                      >
                        {loadingVoci ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        Ricalcola da modello
                      </Button>
                    )}
                  </div>

                  {loadingVoci ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="w-5 h-5 animate-spin text-[#4CAF50]" />
                      <span className="ml-2 text-xs text-slate-500">
                        Calcolo righe da modello...
                      </span>
                    </div>
                  ) : (
                    <>
                      {/* Tabella righe */}
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="text-left px-3 py-2 font-medium text-slate-500">Descrizione</th>
                              <th className="text-center px-3 py-2 font-medium text-slate-500 w-16">Q.tà</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500 w-24">Prezzo unit.</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-500 w-28">IVA</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-500 w-20">Importo</th>
                              <th className="w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {voci.map((v, idx) => (
                              <tr key={idx} className="border-b border-slate-50">
                                <td className="px-3 py-1.5">
                                  <input
                                    className="w-full text-xs border-0 bg-transparent outline-none focus:bg-slate-50 rounded px-1 py-0.5"
                                    value={v.descrizione}
                                    onChange={(e) => updateVoce(idx, "descrizione", e.target.value)}
                                    placeholder="Descrizione voce"
                                  />
                                </td>
                                <td className="px-3 py-1.5">
                                  <input
                                    type="number"
                                    className="w-full text-xs text-center border-0 bg-transparent outline-none focus:bg-slate-50 rounded px-1 py-0.5"
                                    value={v.quantita}
                                    onChange={(e) => updateVoce(idx, "quantita", e.target.value)}
                                    min="1"
                                    step="1"
                                  />
                                </td>
                                <td className="px-3 py-1.5">
                                  <input
                                    type="number"
                                    className="w-full text-xs text-right border-0 bg-transparent outline-none focus:bg-slate-50 rounded px-1 py-0.5"
                                    value={v.prezzo_unitario}
                                    onChange={(e) => updateVoce(idx, "prezzo_unitario", e.target.value)}
                                    min="0"
                                    step="0.01"
                                  />
                                </td>
                                <td className="px-3 py-1.5">
                                  <select
                                    className="w-full text-xs border-0 bg-transparent outline-none focus:bg-slate-50 rounded px-1 py-0.5"
                                    value={v.regime_iva}
                                    onChange={(e) => updateVoce(idx, "regime_iva", e.target.value)}
                                  >
                                    {REGIMI_IVA.map((r) => (
                                      <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-3 py-1.5 text-right font-medium text-slate-700">
                                  €{(
                                    (parseFloat(v.quantita) || 1) *
                                    (parseFloat(v.prezzo_unitario) || 0)
                                  ).toFixed(2)}
                                </td>
                                <td className="px-2 py-1.5">
                                  <button
                                    onClick={() => removeVoce(idx)}
                                    className="text-slate-300 hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {voci.length === 0 && (
                          <div className="text-center py-6 text-slate-400 text-xs">
                            Nessuna voce. Aggiungi voci manualmente o seleziona un modello.
                          </div>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={addVoce}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Aggiungi voce manuale
                      </Button>

                      {/* Totale */}
                      {voci.length > 0 && (
                        <div className="border-t border-slate-100 pt-3 flex justify-end">
                          <div className="text-xs space-y-1 min-w-48">
                            <div className="flex justify-between text-slate-500">
                              <span>Imponibile</span>
                              <span>
                                €{voci
                                  .reduce(
                                    (acc, v) =>
                                      acc +
                                      (parseFloat(v.quantita) || 1) *
                                        (parseFloat(v.prezzo_unitario) || 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between font-semibold text-slate-800 pt-1 border-t border-slate-200">
                              <span>Totale</span>
                              <span className="text-[#4CAF50]">
                                €{voci
                                  .reduce(
                                    (acc, v) =>
                                      acc +
                                      (parseFloat(v.quantita) || 1) *
                                        (parseFloat(v.prezzo_unitario) || 0),
                                    0
                                  )
                                  .toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ─────────── Step 2 — Anteprima ─────────── */}
            {currentStep === 2 && (
              <Card>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-800">Anteprima fattura</h2>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7"
                      onClick={handleAnteprimaPdf}
                      disabled={pdfLoading || !fascicolo || !mese || voci.length === 0}
                    >
                      {pdfLoading ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Eye className="w-3 h-3 mr-1" />
                      )}
                      Anteprima completa
                    </Button>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 text-center">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-xs text-slate-500">
                      Verifica i dati nel pannello di anteprima a destra prima di procedere.
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Per visualizzare il PDF completo usa il pulsante "Anteprima completa".
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
                    <strong>Riepilogo:</strong> Fattura per{" "}
                    <strong>
                      {fascicolo?.nome_completo ||
                        `${fascicolo?.anagrafica?.cognome || ""} ${fascicolo?.anagrafica?.nome || ""}`.trim()}
                    </strong>{" "}
                    — {formatMese(mese)} — {voci.length} voce{voci.length !== 1 ? "i" : ""}
                    {modelloSelezionato && (
                      <span>
                        {" "}— Modello: <strong>{modelloSelezionato.nome}</strong>
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ─────────── Step 3 — Invio ─────────── */}
            {currentStep === 3 && (
              <Card>
                <CardContent className="p-6 space-y-5">
                  {generatedFattura ? (
                    <div className="text-center py-8 space-y-4">
                      <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                        <Check className="w-7 h-7 text-emerald-600" />
                      </div>
                      <div>
                        {generatedFattura._sdiInviata ? (
                          <>
                            <p className="text-lg font-bold text-slate-900">
                              Fattura {generatedFattura.numero} inviata a SDI!
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Totale: €{generatedFattura.totale?.toFixed(2)}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-bold text-slate-900">
                              Bozza creata con successo
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Totale: €{generatedFattura.totale?.toFixed(2)}
                            </p>
                            <p className="text-xs text-amber-600 mt-2 bg-amber-50 border border-amber-100 rounded px-3 py-2">
                              Il numero definitivo verrà assegnato all'approvazione nel Controllo Mensile.
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex justify-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => navigate("/fatturazione")}
                        >
                          Torna all'elenco
                        </Button>
                        <Button
                          size="sm"
                          className="text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
                          onClick={() => navigate("/fatturazione/nuova")}
                        >
                          Nuova fattura
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-sm font-semibold text-slate-800">Conferma e invio</h2>

                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg">
                          <Checkbox
                            id="invia-sdi"
                            checked={inviaSdi}
                            onCheckedChange={setInviaSdi}
                          />
                          <div>
                            <label htmlFor="invia-sdi" className="text-xs font-medium text-slate-700 cursor-pointer">
                              Invia a SDI
                            </label>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              La fattura verrà inviata automaticamente al Sistema di Interscambio dopo la generazione.
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg">
                          <Checkbox
                            id="invia-email"
                            checked={inviaEmail}
                            onCheckedChange={setInviaEmail}
                          />
                          <div>
                            <label htmlFor="invia-email" className="text-xs font-medium text-slate-700 cursor-pointer">
                              Invia email al cliente
                            </label>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              Invia una copia PDF della fattura via email al destinatario (richiede email configurata in Fatture in Cloud).
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
                        Dopo la generazione la fattura non potrà essere modificata nella sua struttura principale.
                        Verifica i dati nel pannello a destra prima di procedere.
                      </div>

                      <Button
                        className="w-full text-xs bg-[#4CAF50] hover:bg-[#388E3C] h-10"
                        onClick={handleGeneraFattura}
                        disabled={generating}
                      >
                        {generating ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Receipt className="w-4 h-4 mr-2" />
                        )}
                        Conferma e genera fattura
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Footer navigazione */}
            {!generatedFattura && (
              <div className="flex items-center justify-between mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => navigate("/fatturazione")}
                >
                  Annulla
                </Button>
                <div className="flex items-center gap-2">
                  {currentStep > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={handleIndietro}
                    >
                      <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                      Indietro
                    </Button>
                  )}
                  {currentStep < STEPS.length - 1 && (
                    <Button
                      size="sm"
                      className="text-xs bg-[#4CAF50] hover:bg-[#388E3C]"
                      onClick={handleAvanti}
                    >
                      Avanti
                      <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Colonna dx — anteprima live */}
          <div className="w-full lg:w-72 xl:w-80 shrink-0">
            <AnteprimePanel
              fascicolo={fascicolo}
              voci={voci}
              mese={mese}
              scadenzaGiorni={scadenzaGiorni}
              causale={causale}
              note={note}
              struttura={struttura}
            />
          </div>
        </div>
      </div>

      {/* Dialog ricerca ospite */}
      <Dialog open={showFascSearch} onOpenChange={setShowFascSearch}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Seleziona ospite</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                className="pl-8 h-8 text-xs"
                placeholder="Cerca per nome o cognome..."
                value={fascSearch}
                onChange={(e) => setFascSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredFascicoli.slice(0, 30).map((f) => (
                <button
                  key={f._id || f.id}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg hover:bg-slate-50 text-left transition-colors"
                  onClick={() => {
                    setFascicolo(f);
                    setShowFascSearch(false);
                    setFascSearch("");
                  }}
                >
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <User className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">
                      {f.nome_completo ||
                        `${f.anagrafica?.cognome || ""} ${f.anagrafica?.nome || ""}`.trim()}
                    </p>
                    {f.anagrafica?.codice_fiscale && (
                      <p className="text-[10px] text-slate-400">
                        {f.anagrafica.codice_fiscale}
                      </p>
                    )}
                  </div>
                </button>
              ))}
              {filteredFascicoli.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-6">Nessun ospite trovato</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog anteprima PDF */}
      <Dialog open={pdfDialogOpen} onOpenChange={handlePdfDialogChange}>
        <DialogContent className="max-w-3xl h-[80vh] p-0">
          <DialogHeader className="px-4 py-3 border-b border-slate-100">
            <DialogTitle className="text-sm">Anteprima fattura PDF</DialogTitle>
          </DialogHeader>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              className="w-full flex-1 h-[calc(80vh-60px)]"
              title="Anteprima PDF"
            />
          ) : (
            <div className="flex items-center justify-center h-[calc(80vh-60px)]">
              <Loader2 className="w-6 h-6 animate-spin text-[#4CAF50]" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
